import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Download, Info } from "lucide-react";
import { api, fmtFecha } from "@/lib/api";
import { hoyISOBA } from "@/components/ClockBA";
import { Avatar, Card, PageHeader, Badge, btnPrim, inputCls } from "@/components/rrhh/ui";

const fmtNum = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

export default function ExportarPage() {
  const [mes, setMes] = useState(hoyISOBA().slice(0, 7));
  const [localId, setLocalId] = useState("");

  const { data: locales } = useQuery({
    queryKey: ["locales"],
    queryFn: async () => (await api.get("/locales")).data,
  });
  const { data } = useQuery({
    queryKey: ["exportar", mes, localId],
    queryFn: async () => (await api.get("/rrhh/exportar", {
      params: { mes, local_id: localId || undefined },
    })).data,
    enabled: !!mes,
  });

  const qs = new URLSearchParams({ mes, ...(localId ? { local_id: localId } : {}) }).toString();

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} data-testid="pagina-exportar">
      <PageHeader
        titulo="Exportar asistencia"
        subtitulo="Los totales del mes de cada persona, en un CSV que Excel abre derecho."
        testid="header-exportar"
      >
        <Badge color="gris" testid="badge-csv">CSV · UTF-8</Badge>
      </PageHeader>

      <div className="grid items-start gap-5 lg:grid-cols-[320px_1fr]">
        <Card titulo="Qué exportar" testid="card-que-exportar">
          <p className="mb-4 text-sm text-gray-400">
            Elegí el mes y el local. La vista previa muestra lo mismo que va a tener el archivo.
          </p>
          <div className="space-y-3.5">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Mes</label>
              <input type="month" value={mes} onChange={(e) => setMes(e.target.value)} className={`${inputCls} w-full`} data-testid="exportar-mes" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Local base</label>
              <select value={localId} onChange={(e) => setLocalId(e.target.value)} className={`${inputCls} w-full`} data-testid="exportar-local">
                <option value="">Todos los locales</option>
                {locales?.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
              </select>
            </div>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/rrhh/exportar.csv?${qs}`}
              className={`${btnPrim} flex w-full items-center justify-center gap-2`}
              data-testid="descargar-csv"
            >
              <Download size={15} /> Descargar el CSV
            </a>
            <div className="rounded-xl border border-[#2DD4BF]/20 bg-[#2DD4BF]/10 p-4" data-testid="que-cuenta">
              <p className="flex items-center gap-2 text-sm font-semibold text-[#2DD4BF]">
                <Info size={14} /> Qué cuenta
              </p>
              <ul className="mt-2 list-inside space-y-1 text-xs leading-relaxed text-gray-300">
                <li>· Los días que ya terminaron: hoy no entra.</li>
                <li>· Las tardanzas cuentan también la de hoy.</li>
                <li>· Una jornada incompleta no suma horas.</li>
              </ul>
            </div>
          </div>
        </Card>

        <Card
          titulo="Vista previa"
          subtitulo={data ? `Del ${fmtFecha(data.rango.desde)} al ${fmtFecha(data.rango.hasta)} · ${data.local_label}` : "…"}
          testid="card-vista-previa"
          bodyCls="p-0"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/8 text-left text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-5 py-3 font-semibold">Persona</th>
                  <th className="px-3 py-3 text-right font-semibold">Días trab.</th>
                  <th className="px-3 py-3 text-right font-semibold">Tardanzas</th>
                  <th className="px-3 py-3 text-right font-semibold">Justif.</th>
                  <th className="px-3 py-3 text-right font-semibold">Faltas</th>
                  <th className="px-3 py-3 text-right font-semibold">Faltas just.</th>
                  <th className="px-3 py-3 text-right font-semibold">Incompl.</th>
                  <th className="px-3 py-3 text-right font-semibold">Horas</th>
                  <th className="px-3 py-3 text-right font-semibold">Comunes</th>
                  <th className="px-3 py-3 text-right font-semibold">Días adic.</th>
                  <th className="px-5 py-3 text-right font-semibold">Horas adic.</th>
                </tr>
              </thead>
              <tbody>
                {data?.filas.map((f) => (
                  <tr key={f.empleado_id} className="border-b border-white/5 last:border-0" data-testid={`exportar-fila-${f.legajo}`}>
                    <td className="min-w-44 px-5 py-3">
                      <span className="flex items-center gap-3">
                        <Avatar iniciales={f.nombre.split(",")[1]?.trim()[0] + f.nombre.split(",")[0][0]} size="sm" />
                        <span className="whitespace-nowrap leading-tight">
                          <span className="block text-sm font-semibold text-white">{f.nombre}</span>
                          <span className="block font-mono-num text-[11px] text-gray-500">Legajo {f.legajo}</span>
                        </span>
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right font-mono-num text-gray-200">{f.dias_trab}</td>
                    <td className={`px-3 py-3 text-right font-mono-num ${f.tardanzas > 0 ? "font-semibold text-amber-300" : "text-gray-500"}`}>{f.tardanzas}</td>
                    <td className="px-3 py-3 text-right font-mono-num text-gray-200">{f.justif}</td>
                    <td className={`px-3 py-3 text-right font-mono-num ${f.faltas > 0 ? "font-semibold text-red-300" : "text-gray-500"}`}>{f.faltas}</td>
                    <td className="px-3 py-3 text-right font-mono-num text-gray-200">{f.faltas_just}</td>
                    <td className="px-3 py-3 text-right font-mono-num text-gray-200">{f.incompl}</td>
                    <td className="px-3 py-3 text-right font-mono-num font-semibold text-white">{fmtNum(f.horas)}</td>
                    <td className="px-3 py-3 text-right font-mono-num text-gray-200">{fmtNum(f.comunes)}</td>
                    <td className="px-3 py-3 text-right font-mono-num text-gray-200">{f.dias_adic}</td>
                    <td className="px-5 py-3 text-right font-mono-num text-gray-200">{fmtNum(f.horas_adic)}</td>
                  </tr>
                ))}
                {data?.filas.length === 0 && (
                  <tr><td colSpan={11} className="px-5 py-10 text-center text-sm text-gray-500">No hay nadie en ese mes y local</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <footer className="flex items-center justify-between border-t border-white/8 px-5 py-3.5 text-xs text-gray-500">
            <span data-testid="exportar-cantidad">{data?.filas.length ?? "…"} personas</span>
            <span className="font-mono-num" data-testid="exportar-archivo">{data?.archivo ?? ""}</span>
          </footer>
        </Card>
      </div>

      <p className="mt-4 text-xs text-gray-500">
        El archivo no lleva PIN, fotos ni motivos: solo los totales del mes de cada persona.
      </p>
    </motion.div>
  );
}
